import { Guid } from "guid-typescript";
import { Attachment } from "./notes-interfaces";
import { FilteredDropDownModel, ListModel } from "shared/interfaces/core-interfaces";
import yup from "../../../../shared/validation/yupExtensions";
import { nullDate } from "shared/utils/common";

export interface ActivityLogsSliceModel {
    activityLog?: ActivityLog,
    activityLogModel: ActivityLogModel,
    activityLogsByEmployee: ActivityLog[],

    statusCode: string,
    message: string,
    showNotification: boolean,
    reloadData: boolean,

    isLoading: boolean,

    filteredPartnersList: FilteredDropDownModel,
    filteredContactsList: FilteredDropDownModel,
}

export interface ActivityLog {
    id: string,
    content: string,
    date: Date,
    createdAt: Date,
    files: Attachment[],
    activityTypeId: number,
    activityType: string,
    contacts: ListModel<string>[] | [],
    companies: ListModel<string>[] | [],
    outcome?: number,
    createdById: string,
    createdByFullName?: string,
    name?: string,
    source?: string,
    sourceId?: string,
    isCommunicationList?: boolean,
    isIncoming?: boolean,
}

export interface CreateActivityLogRequest {
    associatedCompanies: string[] | null,
    associatedContacts: string[] | null,
    content: string,
    date: Date,
    activityTypeId: number,
    outcome: number | null,
    files: Attachment[] | [],
    isIncoming?: boolean,
    sourceId?: string,
    name?: string,
    isCommunicationList?: boolean
}

export interface UpdateActivityLogRequest extends CreateActivityLogRequest {
    id: string,
}

export interface UploadActivityLogFilesRequest {
    logId: string,
    files: Attachment[] | [],
}

export interface ActivityLogFilesResponse {
    id: Guid,
    message: string
    files: Attachment[]
}

// --------------------------------

export const CreateLogRequestSchema = yup.object().shape({
    associatedCompanies: yup.array().of(yup.string().defined()).nullable().default([]),
    associatedContacts: yup.array().of(yup.string().defined()).nullable().default([]),
    content: yup.string().default(""),
    date: yup
        .date()
        .nullable()
        .typeError("Please type valid date.")
        .test("required", "Activity log date is required.", (value) => value !== null),
    activityTypeId: yup.number().required("Activity type is required.").moreThan(0, 'Activity type is required.').transform((value) => Number.isNaN(value) ? null : value),
    outcome: yup
        .object({
            value: yup.number().typeError("Outcome is required.").required("Outcome is required."),
            label: yup.string().required(),
        })
        .transform((value, originalValue) => {
            // Ako originalValue = undefined → vrati null
            return originalValue === undefined ? null : value;
        })
        .nullable()
        .when("activityTypeId", {
            is: (value: number) => value === 2 || value === 3,
            then: (schema) => schema.required("Outcome is required."),
            otherwise: (schema) => schema.nullable().default(null),
        }),
    files: yup.array().of(yup.object({
        id: yup.string().defined(),
        fileName: yup.string().required("File name is required.").default(""),
        description: yup.string().default(""),
        fileBase64: yup.string().required("File content is required.").default(""),
        createdAt: yup.date().required("File creation date is required.").default(new Date()),
        filePrefix: yup.string().required("Prefix string is required.").default(""),
        //isImage: yup.boolean().required("Is image flag is required.").default(false),
    })).default([]),
});

export const UpdateLogRequestSchema = CreateLogRequestSchema.concat(
    yup.object().shape({
        id: yup.string().required().defined(),
    })
);

export interface ActivityLogModel {
    id: string | null,
    associatedCompanies: ListModel<string>[] | [],
    associatedContacts: ListModel<string>[] | [],
    content: string,
    date?: Date | null,
    activityTypeId: number,
    outcome: number | null,
    files: Attachment[] | [],
    source?: string,
    isIncoming?: boolean,
    isCommunicationList?: boolean,
    name: string,
}

export const initialActivityLogValue: ActivityLogModel = {
    id: null,
    name: "",
    associatedCompanies: [],
    associatedContacts: [],
    content: "",
    date: null,
    activityTypeId: 0,
    outcome: null,
    files: [],
}

export const ActivityLogModel = yup.object().shape({
    id: yup.string().defined().nullable(),
    name: yup.string().required("Name is required.").noLeadingSpace(),
    associatedContacts: yup.array().defined(),
    associatedCompanies: yup.array().defined(),
    content: yup.string().default(""),
    date: yup
        .date()
        .nullable()
        .typeError("Please type valid date.")
        .test("required", "Activity log date is required.", (value) => value !== null),
    activityTypeId: yup.number().required("Activity type is required.").moreThan(0, 'Activity type is required.').transform((value) => Number.isNaN(value) ? null : value),
    outcome: yup.number().transform((value) => Number.isNaN(value) ? null : value).when("activityTypeId", {
        is: (value: number) => value === 2 || value === 3,
        then: (schema) => schema.required("Outcome is required.").transform((value) => Number.isNaN(value) ? null : value),
        otherwise: (schema) => schema.notRequired().nullable(),
    }).nullable().default(null),
    files: yup.array().of(yup.object({
        id: yup.string().defined(),
        fileName: yup.string().required("File name is required.").default(""),
        description: yup.string().default(""),
        fileBase64: yup.string().required("File content is required.").default(""),
        createdAt: yup.date().required("File creation date is required.").default(new Date()),
        filePrefix: yup.string().required("Prefix string is required.").default(""),
    })).default([]),
});
